import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputGroupAddon
} from "reactstrap";
import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import useChanged, { usePrevious } from "../../hooks/use-changed";

interface Props {
  show?: boolean;
  sheets: string[];
  onClose?(): void;
  onCreateSheet?(sheetName: string): void;
  onChangeSheet?(sheetName: string): void;
  onRemoveSheet?(sheetName: string): void;
  onRenameSheet?(sheetName: string): void;
}

const SheetsDialog: React.FC<Props> = props => {
  const {
    sheets,
    show,
    onClose,
    onCreateSheet,
    onChangeSheet,
    onRemoveSheet,
    onRenameSheet
  } = props;

  const [sheetName, setSheetName] = useState("");
  const [closing, setClosing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeSheetName, setActiveSheetName] = useState(sheets[0]);

  useEffect(() => setClosing(!show), [show]);

  const prevSheets = usePrevious(sheets);

  const saveActiveSheet = useCallback(
    (ev: React.FormEvent) => {
      ev.preventDefault();
      onRenameSheet!(activeSheetName);
    },
    [activeSheetName, onRenameSheet]
  );

  useChanged(() => {
    setIsEditing(false);
  }, sheets);

  const addSheet = useCallback(
    (ev: React.MouseEvent | React.FormEvent) => {
      ev.preventDefault();
      onCreateSheet!(sheetName);
      setSheetName("");
    },
    [sheetName, onCreateSheet]
  );

  const changeSheet = useCallback(
    (ev: React.MouseEvent, sheetName: string) => {
      ev.preventDefault();
      onChangeSheet!(sheetName);
    },
    [onChangeSheet]
  );

  const removeSheet = useCallback(
    (ev: React.MouseEvent, sheetName: string) => {
      ev.preventDefault();
      if (!confirm(`Timesheet "${sheetName}" wirklich löschen?`)) {
        return;
      }
      onRemoveSheet!(sheetName);
    },
    [onRemoveSheet]
  );

  return (
    <Modal isOpen={!closing} onClosed={onClose}>
      <ModalHeader toggle={() => setClosing(true)}>Timesheets</ModalHeader>
      <ModalBody>
        <s.List>
          {!isEditing ? (
            <li>
              {sheets[0]} (active){" "}
              <Button
                size="sm"
                color="secondary"
                onClick={() => setIsEditing(true)}
              >
                Umbenennen
              </Button>
            </li>
          ) : (
            <li>
              <form onSubmit={saveActiveSheet}>
                <InputGroup>
                  <Input
                    value={activeSheetName}
                    onChange={ev => setActiveSheetName(ev.target.value)}
                  />
                  <InputGroupAddon addonType="append">
                    <Button
                      color="secondary"
                      onClick={() => setIsEditing(true)}
                    >
                      &times;
                    </Button>
                    <Button color="primary">Speichern</Button>
                  </InputGroupAddon>
                </InputGroup>
              </form>
            </li>
          )}
          {sheets.slice(1).map(sheet => (
            <li key={sheet}>
              <a
                href={`?sheet=${sheet}`}
                onClick={ev => changeSheet(ev, sheet)}
              >
                {sheet}
              </a>
              {!prevSheets.includes(sheet) ? " (neu)" : ""}
              <a href="" onClick={ev => removeSheet(ev, sheet)}>
                <FontAwesomeIcon icon={faTrash} />
              </a>
            </li>
          ))}
        </s.List>
        <form onSubmit={addSheet}>
          <InputGroup>
            <Input
              disabled={isEditing}
              placeholder="Name (optional)"
              value={sheetName}
              onChange={ev => setSheetName(ev.target.value)}
            />
            <InputGroupAddon addonType="append">
              <Button color="primary" onClick={addSheet} disabled={isEditing}>
                <FontAwesomeIcon icon={faPlus} /> Hinzufügen
              </Button>
            </InputGroupAddon>
          </InputGroup>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={() => setClosing(true)}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const s = {
  List: styled.ul`
    list-style: none;
    padding-left: 0;

    li {
      display: flex;

      form {
        flex-grow: 1;
      }

      margin-bottom: ${({ theme }) => theme.spacing.md};

      a:first-child {
        margin-right: ${({ theme }) => theme.spacing.xs};
      }

      a:last-child,
      button {
        margin-left: auto;
      }
    }
  `
};

SheetsDialog.defaultProps = {
  onClose() {},
  onCreateSheet() {},
  onRemoveSheet() {},
  onChangeSheet() {},
  onRenameSheet() {},
  show: false
};

export default SheetsDialog;
